import React from 'react';
import { auth, db } from './firebase';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';

export const TEACHER_UID = 'A5QD5uOHthgVzUavF6BeZUOnNZ22';

// Login Component
export const LoginForm = ({ loginForm, setLoginForm, handleLogin, handlePasswordReset }) => {
  return (
    <div className="login-container">
      <div className={`login-box ${loginForm.isSignup ? 'signup-mode' : 'login-mode'}`}>
        <h1>Barefoot Technology Academy</h1>
        <h2>Student Portfolio</h2>
        
        {/* Mode Indicator */}
        <div className="mode-indicator">
          <div 
            className={`mode-tab ${!loginForm.isSignup ? 'active' : ''}`}
            onClick={() => setLoginForm({ ...loginForm, isSignup: false })}
          >            <span>Login</span>
          </div>
          <div 
            className={`mode-tab ${loginForm.isSignup ? 'active' : ''}`}
            onClick={() => setLoginForm({ ...loginForm, isSignup: true })}
          >
                        <span>Sign Up</span>
          </div>
        </div>
        
        <h3>{loginForm.isSignup ? 'Sign Up' : 'Login'}</h3>
        <input
          type="email"
          placeholder="Email"
          value={loginForm.email}
          onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
          onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
        />
        <input
          type="password"
          placeholder="Password"
          value={loginForm.password}
          onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
          onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
        />
        {!loginForm.isSignup && (
          <button
            type="button"
            className="forgot-password-link"
            onClick={() => handlePasswordReset(loginForm.email)}
          >
            Forgot password?
          </button>
        )}
        {loginForm.isSignup && (
          <div className="signup-extra-fields">
            {/* Account Type Toggle */}
            <div className="account-type-toggle">
              <div 
                className={`account-type-tab ${!loginForm.isParent ? 'active' : ''}`}
                onClick={() => setLoginForm({ ...loginForm, isParent: false, linkedStudentUid: '' })}
              >
                <i className="fas fa-user-graduate"></i>
                <span>Student</span>
              </div>
              <div 
                className={`account-type-tab ${loginForm.isParent ? 'active' : ''}`}
                onClick={() => setLoginForm({ ...loginForm, isParent: true })}
              >
                <i className="fas fa-users"></i>
                <span>Parent</span>
              </div>
            </div>
            {loginForm.isParent && (
              <input
                type="text"
                placeholder="Student ID (ask your student to share their ID)"
                value={loginForm.linkedStudentUid || ''}
                onChange={(e) => setLoginForm({ ...loginForm, linkedStudentUid: e.target.value.trim() })}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
              />
            )}
          </div>
        )}
        <button className="primary-action-btn" onClick={handleLogin}>
          <i className={`fas ${loginForm.isSignup ? 'fa-user-plus' : 'fa-sign-in-alt'}`}></i>
          {loginForm.isSignup ? 'Sign Up' : 'Login'}
        </button>
        <button
          className="toggle-btn"
          onClick={() => setLoginForm({ ...loginForm, isSignup: !loginForm.isSignup })}
        >
          <i className={`fas ${loginForm.isSignup ? 'fa-sign-in-alt' : 'fa-user-plus'}`}></i>
          {loginForm.isSignup ? 'Already have an account? Login' : 'Need an account? Sign Up'}
        </button>
      </div>
    </div>
  );
};

// Loading Component
export const AuthLoading = ({ message = "Loading..." }) => {
  return <div className="login-container">{message}</div>;
};

// Verification Prompt Component
// Email verification UI removed per product decision

// Authentication Hook
export const useAuth = () => {
  const [currentUser, setCurrentUser] = React.useState(null);
  const [loadingAuth, setLoadingAuth] = React.useState(true);

  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoadingAuth(false);
    });

    return () => unsubscribe();
  }, []);

  return { currentUser, loadingAuth };
};

// Authentication Functions
export const authFunctions = {
  handleLogin: async (loginForm, setLoginForm) => {
    if (loginForm.isSignup) {
      try {
        const userCredential = await createUserWithEmailAndPassword(auth, loginForm.email, loginForm.password);
        // Create initial user profile immediately after signup
        const isTeacher = userCredential.user.uid === TEACHER_UID;
        const isParent = !!loginForm.isParent;
        const role = isTeacher ? 'teacher' : (isParent ? 'parent' : 'student');
        const profile = {
          name: loginForm.email,
          grade: '',
          profilePicUrl: '',
          role
        };
        if (isParent) {
          // Link to student by UID (serves as ID number in current system)
          profile.linkedStudentUid = (loginForm.linkedStudentUid || '').trim();
        }
        await setDoc(doc(db, 'users', userCredential.user.uid), profile);
        // alert('Account created successfully! You are now logged in.');
      } catch (error) {
        console.error("Error signing up:", error.message);
        alert(`Error creating account: ${error.message}`);
      }
    } else {
      try {
        await signInWithEmailAndPassword(auth, loginForm.email, loginForm.password);
        // alert('Logged in successfully!');
      } catch (error) {
        console.error("Error signing in:", error.message);
        alert(`Error logging in: ${error.message}`);
      }
    }
    setLoginForm({ email: '', password: '', isSignup: false, isParent: false, linkedStudentUid: '' });
  },

  handlePasswordReset: async (email) => {
    try {
      const trimmed = (email || '').trim();
      if (!trimmed) {
        alert('Enter your email above, then click "Forgot password?"');
        return;
      }
      await sendPasswordResetEmail(auth, trimmed);
      alert('Password reset email sent. Check your inbox.');
    } catch (error) {
      console.error('Error sending password reset:', error);
      alert(`Could not send reset email: ${error.message}`);
    }
  },

  // Verification removed per product decision

  handleLogout: async () => {
    try {
      await signOut(auth);
      // alert('Logged out successfully!');
    } catch (error) {
      console.error("Error logging out:", error.message);
      alert(`Error logging out: ${error.message}`);
    }
  }
};

const authComponents = {
  LoginForm,
  AuthLoading,
  useAuth,
  authFunctions
};

export default authComponents;
